'use client';

// Container-width measurement, shared by the two halves of the card projection
// (BL-1438): `DataTableV2` decides table-vs-cards with it, and `TableChrome`
// decides inline-vs-collapsed chrome with it. Extracted rather than copied —
// two implementations of the same measurement would be free to disagree about
// which side of the breakpoint a page is on, and the chrome would collapse
// while the table stayed a table (or the reverse).

import { useEffect, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';
import { PHONE_CARD_BREAKPOINT } from '@auctogon/admin-isomorphic/isomorphic/constants/breakpoints';

/**
 * What a width hook knows: the content-box width, and whether it has measured.
 * `measured` is false only in the server render and the client's first render
 * (BUG-2030) — the first client render is never painted, because the layout
 * effect below measures and re-renders before the browser paints.
 */
interface MeasuredWidth {
    width: number;
    measured: boolean;
}

const UNMEASURED: MeasuredWidth = { width: Number.POSITIVE_INFINITY, measured: false };

/** The element's laid-out content-box width; 0 when it has no layout (hidden, or jsdom). */
function contentBoxWidth(el: HTMLElement): number {
    const box = el.getBoundingClientRect().width;
    if (box === 0) return 0;
    const cs = getComputedStyle(el);
    const inset = (v: string) => Number.parseFloat(v) || 0;
    return box - inset(cs.paddingLeft) - inset(cs.paddingRight) - inset(cs.borderLeftWidth) - inset(cs.borderRightWidth);
}

/**
 * The one measurement both width hooks share (BUG-2030).
 *
 * Reads the laid-out width synchronously in a layout effect, so the first frame
 * the browser paints is already on the right side of a breakpoint — a passive
 * effect painted the desktop layout for a frame or two on every phone. A box
 * with no layout yet (0) is left to the ResizeObserver, whose first report
 * follows; until then it reads as `Infinity`, the desktop layout, as before.
 */
function useMeasuredWidth(target: RefObject<HTMLElement | null> | HTMLElement | null, step?: number): MeasuredWidth {
    const [state, setState] = useState<MeasuredWidth>(UNMEASURED);
    useLayoutEffect(() => {
        const el = target instanceof HTMLElement ? target : target?.current;
        if (!el) return;
        // The observer's first report repeats the width just read; keeping the
        // same state object skips that redundant re-render. With a step, every
        // width inside one step is the same answer, so it skips those too.
        const settle = (raw: number) => {
            const width = step != null ? Math.floor(raw / step) * step : raw;
            setState((prev) => (prev.measured && prev.width === width ? prev : { width, measured: true }));
        };
        const now = contentBoxWidth(el);
        settle(now > 0 ? now : Number.POSITIVE_INFINITY);
        const ro = new ResizeObserver(([entry]) => settle(entry.contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, [target, step]);
    return state;
}

/**
 * Tracks a container's content-box width in px.
 *
 * Starts at `Infinity` — NOT 0 — so a render before the first measurement is
 * treated as "wide", i.e. the desktop layout. Starting at 0 would flash the
 * phone layout on every desktop page load. On the client that render is never
 * painted: the width is read in a layout effect, before paint (BUG-2030). The
 * server render does paint; a surface that must not guess a tier there reads
 * `useContainerTier` instead.
 *
 * Typed on `HTMLElement`, not `HTMLDivElement` — it reads only `contentRect`,
 * so the tag is irrelevant, and the narrower type would have forced a wrapper
 * `<div>` around any non-div consumer (BL-1441: the auction detail's `<header>`).
 *
 * @param ref Ref to the element to measure.
 * @returns The observed content width, or `Infinity` before the first measurement.
 */
export function useContainerWidth(ref: RefObject<HTMLElement | null>): number {
    return useMeasuredWidth(ref).width;
}

/**
 * Which side of a breakpoint a container is on — or `null` while it is not yet
 * measured: the server render, and the client's first render, which is never
 * painted (BUG-2030).
 *
 * Use it where a surface would otherwise guess a tier before it can know one.
 * The server cannot see the viewport, so a guessed `desktop` is what a phone
 * painted until hydration re-measured. Render a tier-neutral placeholder for
 * `null` instead. A container with no layout at mount resolves to `desktop`
 * until its observer reports, so a placeholder never outlives the mount.
 *
 * @param ref Ref to the element to measure.
 * @param breakpoint Width below which the container is `phone`. Defaults to the card breakpoint.
 * @returns `'phone'`, `'desktop'`, or `null` before the first measurement.
 */
export function useContainerTier(
    ref: RefObject<HTMLElement | null>,
    breakpoint: number = PHONE_CARD_BREAKPOINT,
): 'phone' | 'desktop' | null {
    const { width, measured } = useMeasuredWidth(ref);
    if (!measured) return null;
    return width < breakpoint ? 'phone' : 'desktop';
}

/**
 * Tracks an element's content-box width in px, for an element held in state (a
 * callback ref). Use it over `useContainerWidth` when the element mounts after
 * the first render, e.g. behind a permission gate: a ref read in the first
 * effect is still null there and would never be observed (BL-2076).
 *
 * Starts at `Infinity`, like `useContainerWidth`, so an unmeasured page reads
 * as the desktop layout rather than flashing the phone one.
 *
 * @param el The element to measure, or null before it mounts.
 * @param opts.step Report the width rounded down to this many px, so a caller
 *   that only needs it coarsely re-renders once per step of a resize, not per pixel.
 * @returns The observed content width, or `Infinity` before the first measurement.
 */
export function useElementWidth(el: HTMLElement | null, opts?: { step?: number }): number {
    return useMeasuredWidth(el, opts?.step).width;
}

/**
 * Tracks an element's content-box height in px (BL-2074: the ContentDock lifts
 * its toasts by the bulk-bar host's height, which grows when the bar wraps).
 *
 * Takes the element itself rather than a ref, because its one caller holds the
 * element in state (a callback ref) and must re-observe once it mounts. Starts
 * at 0, so an unmeasured host reads as empty rather than as a bar-sized box.
 *
 * @param el The element to measure, or null before it mounts.
 * @returns The observed content height, or 0 before the first measurement.
 */
export function useElementHeight(el: HTMLElement | null): number {
    const [height, setHeight] = useState(0);
    useEffect(() => {
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => setHeight(entry.contentRect.height));
        ro.observe(el);
        return () => ro.disconnect();
    }, [el]);
    return height;
}
